import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import PawPrint from "@/components/PawPrint";
import DisclaimerBanner from "@/components/DisclaimerBanner";

/* ── Thèmes hebdomadaires rotatifs ── */
const WEEKLY_THEMES = [
  { id: "nutrition", icon: "\uD83E\uDD57", label: "Nutrition & alimentation", color: "#27AE60", gradient: "linear-gradient(135deg,rgba(39,174,96,0.12),rgba(39,174,96,0.05))" },
  { id: "hygiene", icon: "\uD83D\uDEC1", label: "Hygiène & soins", color: "#5B8DEF", gradient: "linear-gradient(135deg,rgba(91,141,239,0.12),rgba(91,141,239,0.05))" },
  { id: "activite", icon: "\uD83C\uDFC3", label: "Activité & bien-être", color: "#FF8C42", gradient: "linear-gradient(135deg,rgba(255,140,66,0.12),rgba(255,140,66,0.05))" },
  { id: "prevention", icon: "\uD83D\uDC8A", label: "Prévention & santé", color: "#E53535", gradient: "linear-gradient(135deg,rgba(229,53,53,0.12),rgba(229,53,53,0.05))" },
];

function getWeekNumber() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  return Math.floor((now - start) / (7 * 24 * 60 * 60 * 1000));
}

function getCurrentTheme() {
  return WEEKLY_THEMES[getWeekNumber() % WEEKLY_THEMES.length];
}

const MISSIONS_BY_THEME = {
  nutrition: {
    Chat: [
      { id: "n1", icon: "\uD83E\uDD57", label: "Croquettes adaptées", level: "info", text: "Les croquettes de {name} sont-elles adaptées à son âge et son poids ? Vérifiez l'étiquette." },
      { id: "n2", icon: "\uD83D\uDCA7", label: "Hydratation", level: "warning", text: "Les chats boivent souvent trop peu. Vérifiez que {name} a une fontaine ou un bol d'eau fraîche." },
      { id: "n3", icon: "\u2696\uFE0F", label: "Portions mesurées", level: "info", text: "Respectez-vous les doses journalières pour {name} ? Le surpoids félin est un risque majeur." },
      { id: "n4", icon: "\uD83D\uDEAB", label: "Aliments interdits", level: "warning", text: "Rappel : chocolat, oignon, raisin, lait de vache et ail sont toxiques pour {name}." },
    ],
    Chien: [
      { id: "n1", icon: "\uD83C\uDF56", label: "Alimentation équilibrée", level: "info", text: "La ration de {name} est-elle adaptée à sa taille et son activité physique ?" },
      { id: "n2", icon: "\uD83D\uDCA7", label: "Eau fraîche", level: "ok", text: "{name} a-t-il toujours accès à de l'eau propre, surtout après l'effort ?" },
      { id: "n3", icon: "\uD83C\uDF6C", label: "Friandises raisonnables", level: "warning", text: "Les friandises ne doivent pas dépasser 10% de l'apport calorique de {name}." },
      { id: "n4", icon: "\uD83E\uDD69", label: "Os et restes", level: "warning", text: "Évitez les os cuits et les restes de table pour {name} — risque d'occlusion." },
    ],
    Lapin: [
      { id: "n1", icon: "\uD83C\uDF3E", label: "80% de foin", level: "warning", text: "Le foin doit constituer 80% de l'alimentation de {name}. Est-il toujours disponible ?" },
      { id: "n2", icon: "\uD83E\uDD57", label: "Légumes frais", level: "info", text: "Proposez des légumes frais à {name} : persil, endive, fenouil. Pas de laitue iceberg." },
      { id: "n3", icon: "\uD83C\uDF52", label: "Fruits avec modération", level: "info", text: "Les fruits sont des friandises pour {name} — max 1 cuillère/jour à cause du sucre." },
      { id: "n4", icon: "\uD83D\uDCA9", label: "Cæcotrophes", level: "ok", text: "{name} mange-t-il bien ses cæcotrophes (crottes molles) ? C'est essentiel à sa nutrition." },
    ],
  },
  hygiene: {
    Chat: [
      { id: "h1", icon: "\uD83D\uDEC1", label: "Brossage pelage", level: "ok", text: "Brossez {name} au moins 2 fois/semaine pour éviter les boules de poils." },
      { id: "h2", icon: "\uD83D\uDC42", label: "Oreilles propres", level: "info", text: "Vérifiez les oreilles de {name} — pas de cérumen noir ni d'odeur suspecte." },
      { id: "h3", icon: "\uD83D\uDC41\uFE0F", label: "Yeux nets", level: "ok", text: "Les yeux de {name} sont-ils clairs, sans écoulement ? Nettoyez si nécessaire." },
      { id: "h4", icon: "\uD83E\uDDB7", label: "Tartre dentaire", level: "warning", text: "Inspectez les gencives de {name}. Rougeurs ou mauvaise haleine = visite vétérinaire." },
    ],
    Chien: [
      { id: "h1", icon: "\uD83E\uDDB7", label: "Brossage des dents", level: "warning", text: "Brossez les dents de {name} 2-3 fois/semaine avec un dentifrice vétérinaire." },
      { id: "h2", icon: "\uD83D\uDEC1", label: "Bain mensuel", level: "info", text: "Un bain par mois suffit pour {name}. Utilisez un shampoing pH neutre pour chien." },
      { id: "h3", icon: "\u2702\uFE0F", label: "Griffes", level: "warning", text: "Les griffes de {name} cliquettent au sol ? Il est temps de les couper ou faire couper." },
      { id: "h4", icon: "\uD83D\uDC42", label: "Oreilles", level: "info", text: "Nettoyez les oreilles de {name} avec un produit adapté, surtout s'il a les oreilles tombantes." },
    ],
    Lapin: [
      { id: "h1", icon: "\uD83C\uDFE0", label: "Litière propre", level: "warning", text: "Nettoyez la litière de {name} tous les 2 jours minimum. Hygiène = santé." },
      { id: "h2", icon: "\uD83D\uDEC1", label: "Pelage", level: "ok", text: "Brossez {name} régulièrement, surtout en période de mue (printemps/automne)." },
      { id: "h3", icon: "\u2702\uFE0F", label: "Griffes", level: "info", text: "Vérifiez les griffes de {name} — coupez-les si elles sont trop longues." },
      { id: "h4", icon: "\uD83D\uDC42", label: "Zone périnéale", level: "warning", text: "Vérifiez que la zone arrière de {name} est propre — risque de myiase en été." },
    ],
  },
  activite: {
    Chat: [
      { id: "a1", icon: "\uD83C\uDFAE", label: "Séances de jeu", level: "info", text: "Jouez avec {name} au moins 2×10 min/jour. Les chats d'intérieur en ont besoin." },
      { id: "a2", icon: "\uD83C\uDFE0", label: "Enrichissement", level: "ok", text: "{name} a-t-il des arbres à chat, cachettes et jouets variés ? Changez-les régulièrement." },
      { id: "a3", icon: "\uD83D\uDE34", label: "Qualité du sommeil", level: "ok", text: "{name} dort 12-16h/jour. Un changement dans ses habitudes peut indiquer un souci." },
      { id: "a4", icon: "\u2696\uFE0F", label: "Pesée hebdomadaire", level: "warning", text: "Pesez {name} cette semaine. Un changement brusque de poids nécessite un avis vétérinaire." },
    ],
    Chien: [
      { id: "a1", icon: "\uD83C\uDFC3", label: "Promenade quotidienne", level: "warning", text: "{name} a-t-il eu au moins 45 min d'exercice par jour cette semaine ?" },
      { id: "a2", icon: "\uD83E\uDDE0", label: "Stimulation mentale", level: "info", text: "Proposez un jouet d'occupation (Kong, snuffle mat) à {name} pour stimuler son intellect." },
      { id: "a3", icon: "\uD83D\uDC3E", label: "Socialisation", level: "ok", text: "{name} a-t-il pu rencontrer d'autres chiens ou personnes cette semaine ?" },
      { id: "a4", icon: "\u2696\uFE0F", label: "Pesée hebdomadaire", level: "warning", text: "Pesez {name} cette semaine — le suivi régulier détecte les problèmes tôt." },
    ],
    Lapin: [
      { id: "a1", icon: "\uD83C\uDFE1", label: "Liberté quotidienne", level: "warning", text: "{name} doit sortir de son enclos au moins 4h/jour. A-t-il eu assez de liberté ?" },
      { id: "a2", icon: "\uD83E\uDDF8", label: "Jouets", level: "ok", text: "Tunnels, balles en osier, rouleaux... {name} a-t-il de quoi s'occuper et ronger ?" },
      { id: "a3", icon: "\uD83D\uDC30", label: "Comportement", level: "info", text: "{name} fait-il des binkies (sauts de joie) ? C'est signe de bien-être !" },
      { id: "a4", icon: "\u2696\uFE0F", label: "Pesée hebdomadaire", level: "warning", text: "Pesez {name} cette semaine — les lapins cachent bien leur maladie." },
    ],
  },
  prevention: {
    Chat: [
      { id: "p1", icon: "\uD83D\uDC89", label: "Rappel vaccinal", level: "warning", text: "Les vaccins de {name} sont-ils à jour ? Typhus, coryza, leucose — vérifiez le carnet." },
      { id: "p2", icon: "\uD83D\uDC1B", label: "Antiparasitaire", level: "warning", text: "Le traitement antipuce/tique de {name} est-il à jour ? Renouvellement mensuel." },
      { id: "p3", icon: "\uD83E\uDE7A", label: "Vermifuge", level: "info", text: "Quand date le dernier vermifuge de {name} ? 2 à 4 fois/an pour un chat adulte." },
      { id: "p4", icon: "\uD83D\uDCCB", label: "Bilan annuel", level: "info", text: "Planifiez la visite annuelle de {name} chez le vétérinaire — même s'il va bien." },
    ],
    Chien: [
      { id: "p1", icon: "\uD83D\uDC89", label: "Rappel vaccinal", level: "warning", text: "Vaccins de {name} à jour ? CHPL, rage, leptospirose — vérifiez le carnet." },
      { id: "p2", icon: "\uD83D\uDC1B", label: "Antiparasitaire", level: "warning", text: "Traitez {name} contre puces et tiques chaque mois, surtout au printemps/été." },
      { id: "p3", icon: "\uD83E\uDE7A", label: "Vermifuge", level: "info", text: "Vermifugez {name} 2 à 4 fois par an. Plus si contact avec des enfants." },
      { id: "p4", icon: "\uD83C\uDFE5", label: "Visite annuelle", level: "info", text: "La visite annuelle de {name} est-elle programmée ? Bilan sanguin après 7 ans." },
    ],
    Lapin: [
      { id: "p1", icon: "\uD83D\uDC89", label: "VHD & Myxomatose", level: "warning", text: "Les vaccins VHD et myxomatose de {name} sont-ils à jour ? Rappel annuel." },
      { id: "p2", icon: "\uD83E\uDDB7", label: "Dents", level: "info", text: "Les dents de {name} poussent en continu. Vérifiez l'alignement et l'usure régulièrement." },
      { id: "p3", icon: "\uD83E\uDE7A", label: "Vermifuge", level: "info", text: "Vermifugez {name} 2 fois/an, surtout s'il a accès à l'extérieur." },
      { id: "p4", icon: "\uD83C\uDFE5", label: "Visite semestrielle", level: "warning", text: "Les lapins doivent voir un vétérinaire NAC 2 fois/an — {name} a-t-il un RDV ?" },
    ],
  },
};

const DEFAULT_MISSIONS = {
  nutrition: [
    { id: "n1", icon: "\uD83E\uDD57", label: "Alimentation adaptée", level: "info", text: "L'alimentation de {name} est-elle adaptée à son espèce et son âge ?" },
    { id: "n2", icon: "\uD83D\uDCA7", label: "Eau fraîche", level: "ok", text: "Vérifiez que {name} a toujours accès à de l'eau propre." },
    { id: "n3", icon: "\u2696\uFE0F", label: "Pesée", level: "warning", text: "Pesez {name} cette semaine pour suivre son poids." },
  ],
  hygiene: [
    { id: "h1", icon: "\uD83D\uDEC1", label: "Propreté", level: "ok", text: "L'espace de vie de {name} est-il propre et confortable ?" },
    { id: "h2", icon: "\uD83D\uDC42", label: "Inspection", level: "info", text: "Inspectez les oreilles, yeux et pelage de {name} cette semaine." },
    { id: "h3", icon: "\u2702\uFE0F", label: "Griffes", level: "info", text: "Vérifiez les griffes de {name} — coupez-les si nécessaire." },
  ],
  activite: [
    { id: "a1", icon: "\uD83C\uDFC3", label: "Exercice", level: "warning", text: "{name} a-t-il eu assez d'activité cette semaine ?" },
    { id: "a2", icon: "\uD83E\uDDE0", label: "Stimulation", level: "info", text: "Proposez des jeux ou jouets variés à {name}." },
    { id: "a3", icon: "\u2696\uFE0F", label: "Pesée", level: "warning", text: "Pesez {name} cette semaine." },
  ],
  prevention: [
    { id: "p1", icon: "\uD83D\uDC89", label: "Vaccins", level: "warning", text: "Les vaccins de {name} sont-ils à jour ?" },
    { id: "p2", icon: "\uD83D\uDC1B", label: "Antiparasitaire", level: "warning", text: "Le traitement antiparasitaire de {name} est-il à jour ?" },
    { id: "p3", icon: "\uD83C\uDFE5", label: "Visite vétérinaire", level: "info", text: "Quand date la dernière visite de {name} chez le vétérinaire ?" },
  ],
};

const EMOJI_MAP = { Chat: "\ud83d\udc31", Chien: "\ud83d\udc36", Lapin: "\ud83d\udc30", Oiseau: "\ud83d\udc26", Rongeur: "\ud83d\udc39", Reptile: "\ud83e\udd8e", Autre: "\ud83d\udc3e" };
const COLORS_MAP = { Chat: "#FFB7C5", Chien: "#FBBF59", Lapin: "#A8D8EA", Oiseau: "#C3E8FF", Rongeur: "#D4C5F0", Reptile: "#B5E8C0", Autre: "#E0E0E0" };

function enrichAnimalForHome(a) {
  const birthdate = a.birthdate || "";
  let age = "?";
  if (birthdate) {
    const years = new Date().getFullYear() - new Date(birthdate).getFullYear();
    age = years <= 0 ? "< 1 an" : `${years} ans`;
  }
  const theme = getCurrentTheme();
  const themeMissions = MISSIONS_BY_THEME[theme.id] || {};
  const templates = themeMissions[a.species] || DEFAULT_MISSIONS[theme.id] || DEFAULT_MISSIONS.nutrition;
  const suggestions = templates.map(s => ({ ...s, text: s.text.replace(/\{name\}/g, a.name) }));
  return {
    ...a,
    age,
    emoji: EMOJI_MAP[a.species] || "\ud83d\udc3e",
    color: COLORS_MAP[a.species] || "#E0E0E0",
    bgGradient: `linear-gradient(135deg, ${COLORS_MAP[a.species] || "#E0E0E0"}44 0%, ${COLORS_MAP[a.species] || "#E0E0E0"} 100%)`,
    suggestions,
  };
}

const LEVEL = {
  ok:      { bg: "bg-severity-green-bg", border: "#6FCF97", dot: "#27AE60", badge: "\u2713 Bon" },
  info:    { bg: "bg-[#EEF4FF]",         border: "#90B4FF", dot: "#5B8DEF", badge: "\u2139 Conseil" },
  warning: { bg: "bg-severity-yellow-bg", border: "#F9C74F", dot: "#F0A500", badge: "! \u00C0 faire" },
};

/* ====== COMPOSANTS ====== */

function AnimalCard({ animal, isSelected, onSelect, doneCount, totalCount }) {
  const pct = totalCount === 0 ? 0 : Math.round((doneCount / totalCount) * 100);

  return (
    <div
      onClick={() => onSelect(animal)}
      className={cn(
        "rounded-5xl px-4 pt-5 pb-3.5 cursor-pointer min-w-[148px] flex-[1_0_138px] max-w-[200px]",
        "relative transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
        "hover:-translate-y-1.5 hover:scale-[1.03]",
        isSelected ? "border-[3px]" : "border-[3px] border-transparent",
      )}
      style={{
        background: animal.bgGradient,
        borderColor: isSelected ? animal.color : "transparent",
        boxShadow: isSelected
          ? `0 12px 40px ${animal.color}55`
          : "0 4px 18px rgba(0,0,0,0.06)",
      }}
    >
      {/* Species badge */}
      <div className="absolute top-2.5 right-3 bg-white/75 rounded-full px-2.5 py-0.5 text-[11px] font-bold text-gray-500">
        {animal.species}
      </div>

      {/* Avatar */}
      <div className={cn(
        "w-[74px] h-[74px] rounded-full bg-white/60 flex items-center justify-center",
        "text-4xl mx-auto mb-2.5 shadow-soft border-[3px] border-white/90 overflow-hidden",
        "transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
        "group-hover:-rotate-[5deg] group-hover:scale-[1.08]",
      )}>
        {animal.photo_url
          ? <img src={animal.photo_url} alt={animal.name} className="w-full h-full object-cover" />
          : animal.emoji}
      </div>

      {/* Name & breed */}
      <div className="font-extrabold text-base text-app-text text-center">{animal.name}</div>
      <div className="text-[11px] text-gray-500 text-center mt-0.5">{animal.breed} &middot; {animal.age}</div>

      {/* Progress bar */}
      <div className="mt-2.5 h-[5px] rounded-full bg-white/50 overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-[600ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]"
          style={{
            width: `${pct}%`,
            background: pct === 100 ? "#27AE60" : "linear-gradient(90deg,#FFB347,#FF5C8D)",
          }}
        />
      </div>
      <div className="text-[10px] text-gray-400 text-center mt-1 font-bold">
        {doneCount}/{totalCount} missions
      </div>
    </div>
  );
}

function PawCareScore({ total, done, name, emoji }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const stars = pct === 100 ? 3 : pct >= 60 ? 2 : pct >= 25 ? 1 : 0;

  const gradients = [
    "linear-gradient(135deg,#E0E0E0,#BDBDBD)",
    "linear-gradient(135deg,#FFB347,#FF7055)",
    "linear-gradient(135deg,#FFB347,#FF7055,#FF5C8D)",
    "linear-gradient(135deg,#FFD700,#FF8C42,#FF5C8D)",
  ];
  const colors = ["#AAAAAA", "#FF8C42", "#FF7055", "#FF5C8D"];
  const msgs = [
    "Compl\u00E9tez vos missions pour booster le score ! \uD83C\uDF31",
    "Bon d\u00E9but, continuez sur cette lanc\u00E9e ! \uD83C\uDF1F",
    "Presque l\u00E0, encore un effort ! \uD83D\uDCAA",
    "Score parfait cette semaine ! \uD83C\uDFC6\uD83C\uDF89",
  ];

  return (
    <div className="bg-white rounded-4xl px-5 py-4 shadow-[0_4px_20px_rgba(255,112,85,0.1)] border-[1.5px] border-pawcare-orange/20 mb-5">
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div
            className="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center text-lg"
            style={{ background: gradients[stars] }}
          >
            {"\uD83D\uDC3E"}
          </div>
          <div>
            <div className="font-black text-sm text-app-text">Score PawCare</div>
            <div className="text-[11px] text-app-subtle font-semibold">{name} {emoji} &middot; cette semaine</div>
          </div>
        </div>
        <div className="font-display text-[32px] leading-none" style={{ color: colors[stars] }}>
          {pct}%
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden mb-2.5">
        <div
          className="h-full rounded-full transition-[width] duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
          style={{
            width: `${pct}%`,
            background: gradients[stars],
            boxShadow: pct > 0 ? "0 2px 8px rgba(255,140,66,0.4)" : "none",
          }}
        />
      </div>

      {/* Stars + message */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className="text-lg transition-[filter] duration-[400ms] ease-in-out"
              style={{ filter: i < stars ? "none" : "grayscale(1) opacity(0.3)" }}
            >
              {"\u2B50"}
            </span>
          ))}
        </div>
        <span className="text-[11.5px] text-app-muted font-bold">{msgs[stars]}</span>
      </div>
    </div>
  );
}

function SuggestionCard({ s, delay, done, onToggle, onSkip }) {
  const lv = LEVEL[s.level];

  return (
    <div
      className={cn(
        "rounded-3xl p-3.5 flex items-center gap-3 transition-all duration-300 ease-in-out border-[1.5px]",
        done ? "bg-[#F0FFF6] border-[#6FCF97]" : lv.bg,
      )}
      style={{
        borderColor: done ? "#6FCF97" : lv.border,
        animation: `slideUp 0.4s cubic-bezier(0.34,1.56,0.64,1) ${delay}ms both`,
      }}
    >
      {/* Icon */}
      <div
        className={cn(
          "w-10 h-10 rounded-[13px] shrink-0 flex items-center justify-center text-[19px] shadow-[0_2px_8px_rgba(0,0,0,0.07)]",
          done ? "bg-[rgba(111,207,151,0.18)]" : "bg-white/75",
        )}
      >
        {done ? "\u2705" : s.icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-[7px] mb-0.5 flex-wrap">
          <span
            className={cn(
              "font-extrabold text-[13px]",
              done ? "text-severity-green line-through" : "text-app-text",
            )}
          >
            {s.label}
          </span>
          {done ? (
            <span className="bg-[#6FCF9720] text-severity-green rounded-full px-2 py-px text-[11px] font-bold">
              {"\u2713"} Fait !
            </span>
          ) : (
            <span
              className="rounded-full px-2 py-px text-[11px] font-bold"
              style={{
                background: lv.border + "28",
                color: lv.dot,
              }}
            >
              {lv.badge}
            </span>
          )}
        </div>
        <p className={cn("text-[12.5px] leading-normal m-0", done ? "text-[#6FCF97]" : "text-gray-600")}>
          {s.text}
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col gap-[5px] shrink-0">
        {/* Toggle done */}
        <button
          onClick={onToggle}
          title={done ? "Annuler" : "Fait !"}
          className={cn(
            "w-[30px] h-[30px] rounded-full cursor-pointer flex items-center justify-center",
            "text-[13px] font-black transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
            done ? "border-none text-white scale-110" : "border-[1.5px]",
          )}
          style={{
            background: done
              ? "linear-gradient(135deg,#6FCF97,#27AE60)"
              : "rgba(255,255,255,0.9)",
            borderColor: done ? undefined : lv.border,
            boxShadow: done
              ? "0 3px 10px rgba(111,207,151,0.5)"
              : `0 2px 8px ${lv.border}44`,
            color: done ? "white" : lv.dot,
          }}
        >
          {done ? "\u2713" : "\u25CB"}
        </button>

        {/* Skip */}
        {!done && (
          <button
            onClick={onSkip}
            title="Passer \u2014 ne me concerne pas"
            className={cn(
              "w-[30px] h-[30px] rounded-full cursor-pointer flex items-center justify-center",
              "text-[13px] bg-white/80 border-[1.5px] border-gray-200 text-gray-300",
              "transition-all duration-200 ease-in-out",
              "hover:border-pawcare-orange hover:text-pawcare-orange-dark hover:bg-[#FFF8F0]",
            )}
          >
            {"\u2192"}
          </button>
        )}
      </div>
    </div>
  );
}

function SkippedSection({ items, onUnskipOne, onUnskipAll }) {
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;

  return (
    <div className="mb-3">
      <button
        onClick={() => setOpen(v => !v)}
        className="bg-transparent border-none cursor-pointer py-1.5 px-0.5 flex items-center gap-2 font-bold text-xs text-gray-400"
      >
        <span
          className="inline-block transition-transform duration-200"
          style={{ transform: open ? "rotate(90deg)" : "rotate(0)" }}
        >
          {"\u25B6"}
        </span>
        {items.length} mission{items.length > 1 ? "s" : ""} ignorée{items.length > 1 ? "s" : ""}
      </button>

      {open && (
        <div className="bg-gray-50/90 rounded-2xl px-3.5 py-2.5 mt-1.5 border-[1.5px] border-dashed border-gray-200 animate-slide-up">
          {items.map(s => (
            <div
              key={s.id}
              className="flex items-center gap-2.5 py-[7px] border-b border-black/[0.04]"
            >
              <span className="text-base opacity-40">{s.icon}</span>
              <span className="flex-1 text-[12.5px] text-app-muted font-bold">{s.label}</span>
              <button
                onClick={() => onUnskipOne(s.id)}
                className={cn(
                  "bg-white border-[1.5px] border-gray-200 rounded-full px-3 py-0.5",
                  "text-[11px] font-bold text-gray-400 cursor-pointer transition-all duration-150",
                  "hover:border-pawcare-orange hover:text-pawcare-orange-dark",
                )}
              >
                Rétablir
              </button>
            </div>
          ))}
          {items.length > 1 && (
            <button
              onClick={onUnskipAll}
              className={cn(
                "mt-2 w-full bg-transparent border-[1.5px] border-gray-200 rounded-xl py-[7px]",
                "text-xs font-bold text-gray-400 cursor-pointer",
                "hover:border-pawcare-orange hover:text-pawcare-orange-dark",
              )}
            >
              {"\u21A9"} Rétablir toutes
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ====== PAGE ====== */

export default function HomePage() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [animals, setAnimals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pawcare_missions_done") || "{}"); } catch { return {}; }
  });
  const [skipped, setSkipped] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pawcare_missions_skipped") || "{}"); } catch { return {}; }
  });

  const [showFeedback, setShowFeedback] = useState(false);
  const [fbRating, setFbRating] = useState(0);
  const [fbComment, setFbComment] = useState("");
  const [fbSent, setFbSent] = useState(false);
  const [fbLoading, setFbLoading] = useState(false);

  useEffect(() => { localStorage.setItem("pawcare_missions_done", JSON.stringify(done)); }, [done]);
  useEffect(() => { localStorage.setItem("pawcare_missions_skipped", JSON.stringify(skipped)); }, [skipped]);

  // Load existing feedback
  useEffect(() => {
    API.get("/api/feedback").then(res => {
      if (res.data) {
        setFbRating(res.data.rating);
        setFbComment(res.data.comment || "");
        setFbSent(true);
      }
    }).catch(() => {});
  }, [API]);

  const submitFeedback = async () => {
    if (fbRating === 0) return;
    setFbLoading(true);
    try {
      await API.post("/api/feedback", { rating: fbRating, comment: fbComment });
      setFbSent(true);
    } catch { /* silent */ }
    setFbLoading(false);
  };

  useEffect(() => {
    API.get("/api/animals").then(res => {
      const enriched = res.data.map(enrichAnimalForHome);
      setAnimals(enriched);
      if (enriched.length > 0) setSelected(enriched[0]);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  const toggleDone = (aid, sid) => {
    const wasDone = !!done[aid]?.[sid];
    setDone(p => ({ ...p, [aid]: { ...(p[aid] || {}), [sid]: !wasDone } }));
    // Mark inactivity timer reset only when marking as done (not when un-checking)
    if (!wasDone) {
      API.post("/api/notifications/missions/touch").catch(() => {});
    }
  };

  const skipOne = (aid, sid) => {
    setSkipped(p => ({ ...p, [aid]: { ...(p[aid] || {}), [sid]: true } }));
    setDone(p => ({ ...p, [aid]: { ...(p[aid] || {}), [sid]: false } }));
  };

  const unskipOne = (aid, sid) =>
    setSkipped(p => ({ ...p, [aid]: { ...(p[aid] || {}), [sid]: false } }));

  const unskipAll = (aid) =>
    setSkipped(p => ({ ...p, [aid]: {} }));

  const activeFor  = (a) => (a?.suggestions || []).filter(s => !skipped[a.id]?.[s.id]);
  const skippedFor = (a) => (a?.suggestions || []).filter(s =>  skipped[a.id]?.[s.id]);
  const doneCount  = (a) => a ? activeFor(a).filter(s => done[a.id]?.[s.id]).length : 0;
  const totalCount = (a) => a ? activeFor(a).length : 0;

  if (animals.length === 0) {
    return (
      <div className="min-h-screen bg-app-gradient flex flex-col items-center justify-center px-8 text-center">
        <div className="text-5xl mb-4">{"\ud83d\udc3e"}</div>
        <div className="font-display text-xl text-app-text mb-2">Bienvenue sur PawCare !</div>
        <p className="text-sm text-app-muted font-semibold mb-6">
          Ajoutez votre premier animal pour commencer.
        </p>
        <button
          onClick={() => navigate("/animaux")}
          className="bg-pawcare-gradient text-white font-black text-base px-6 py-3.5 rounded-2xl border-none cursor-pointer shadow-pawcare"
        >
          + Ajouter un animal
        </button>
      </div>
    );
  }

  if (!selected) return null;

  return (
    <div className="min-h-screen bg-app-gradient animate-fade-in">

      {/* HEADER */}
      <div className="bg-pawcare-gradient px-6 py-5 flex items-center justify-between shadow-pawcare" style={{ paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}>
        <div className="flex items-center gap-2.5">
          <PawPrint size={36} color="rgba(255,255,255,0.92)" />
          <div>
            <div className="font-display text-2xl text-white leading-none [text-shadow:0_2px_8px_rgba(0,0,0,0.12)]">
              PawCare
            </div>
            <div className="text-[11px] text-white/75 font-semibold">
              Bonjour ! On s'occupe de vos loulous {"\uD83D\uDC95"}
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowFeedback(true)}
          className="rounded-[14px] bg-white/[0.22] flex items-center gap-1.5 px-3 py-2 cursor-pointer border-[1.5px] border-white/35 backdrop-blur-sm animate-bounce3"
          style={{ animationDuration: "2s", animationIterationCount: "infinite" }}
        >
          <span className="text-lg">{"\u2B50"}</span>
          <span className="text-[11px] text-white font-extrabold leading-tight">Aidez-moi a{"\n"}ameliorer l'app</span>
        </button>
      </div>

      {/* CONTENU */}
      <div className="px-5 pt-5 max-w-[540px] mx-auto">

        <h2 className="font-black text-[17px] text-app-text mb-3.5">
          Mes loulous {"\uD83D\uDC3E"}
        </h2>

        {/* Animal cards carousel */}
        <div className="flex gap-3.5 overflow-x-auto pb-3 mb-6">
          {animals.map(a => (
            <AnimalCard
              key={a.id}
              animal={a}
              isSelected={selected?.id === a.id}
              onSelect={(animal) => navigate("/animaux", { state: { animalId: animal.id } })}
              doneCount={doneCount(a)}
              totalCount={totalCount(a)}
            />
          ))}
        </div>

        <div className="animate-fade-in">
          {/* Weekly focus banner */}
          {(() => { const theme = getCurrentTheme(); return (
          <div
            className="border-[1.5px] rounded-[18px] px-4 py-3 mb-[18px] flex items-center justify-between"
            style={{ background: theme.gradient, borderColor: theme.color + "40" }}
          >
            <span className="font-extrabold text-[13.5px]" style={{ color: theme.color }}>
              {theme.icon} {theme.label}
            </span>
            <span className="text-[11px] text-app-subtle font-semibold">
              Sem. du {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
            </span>
          </div>
          ); })()}

          {/* PawCare score widget */}
          <PawCareScore
            total={totalCount(selected)}
            done={doneCount(selected)}
            name={selected?.name || ""}
            emoji={selected?.emoji || "\ud83d\udc3e"}
          />

          {/* Missions header */}
          <div className="flex items-center justify-between mb-3.5">
            <h3 className="font-black text-base text-app-text">
              Missions de {selected.name} {"\u2728"}
            </h3>
            <span className="text-xs text-app-subtle font-bold">
              {doneCount(selected)}/{totalCount(selected)} faites
            </span>
          </div>

          {/* Active missions */}
          <div className="flex flex-col gap-2.5 mb-3">
            {activeFor(selected).length === 0 ? (
              <div className="text-center py-6 px-4 text-app-subtle font-bold text-sm bg-white/50 rounded-[18px] border-[1.5px] border-dashed border-gray-200">
                Toutes les missions ont été ignorées.
                <br />
                <span className="text-xs">Rétablissez-en ci-dessous {"\uD83D\uDC47"}</span>
              </div>
            ) : (
              activeFor(selected).map((s, i) => (
                <SuggestionCard
                  key={s.id}
                  s={s}
                  delay={i * 70}
                  done={!!(done[selected.id]?.[s.id])}
                  onToggle={() => toggleDone(selected.id, s.id)}
                  onSkip={() => skipOne(selected.id, s.id)}
                />
              ))
            )}
          </div>

          {/* Skipped missions */}
          <SkippedSection
            items={skippedFor(selected)}
            onUnskipOne={(sid) => unskipOne(selected.id, sid)}
            onUnskipAll={() => unskipAll(selected.id)}
          />

          {/* CTA card */}
          <div className="bg-white/[0.72] backdrop-blur-[14px] border-[1.5px] border-pawcare-orange/25 rounded-4xl px-5 py-[18px] shadow-[0_8px_32px_rgba(255,112,85,0.1)] mb-7">
            <p className="text-[13px] text-app-muted text-center mb-3.5 font-semibold">
              Un doute ? Un symptôme ? L'IA vétérinaire est là 24h/24 {"\uD83D\uDC95"}
            </p>
            <button
              onClick={() => navigate("/chat")}
              className={cn(
              "w-full py-4 rounded-[18px] border-none cursor-pointer bg-pawcare-gradient",
              "text-white font-black text-base shadow-pawcare",
              "flex items-center justify-center gap-2.5",
              "transition-all duration-200",
              "hover:scale-[1.02] hover:shadow-pawcare-lg active:scale-[0.98]",
            )}>
              <span className="text-[22px]">{"\uD83E\uDE7A"}</span>
              Consulter pour {selected.name}
              <span className="text-lg">{"\u2192"}</span>
            </button>
          </div>
        </div>

        {/* Quick access grid */}
        <h3 className="font-black text-base text-app-text mb-3.5">
          Accès rapide {"\u26A1"}
        </h3>
        <div className="grid grid-cols-2 gap-3 mb-7">
          {[
            { emoji: "\uD83D\uDCCB", label: "Historique",  sub: "Consultations pass\u00e9es", bg: "linear-gradient(135deg,#E8F0FF,#C5D8FF)", action: () => navigate("/historique") },
            { emoji: "\uD83D\uDEA8", label: "Urgences",    sub: "V\u00E9t\u00E9rinaires proches", bg: "linear-gradient(135deg,#FFE8E8,#FFC5C5)", action: () => navigate("/urgences") },
            { emoji: "\uD83D\uDCC5", label: "Rappels",     sub: "Bient\u00f4t disponible",   bg: "linear-gradient(135deg,#FFF4DC,#FFE4A0)", action: null },
            { emoji: "\uD83C\uDF21\uFE0F", label: "Suivi poids", sub: "Voir mes animaux",       bg: "linear-gradient(135deg,#E8FFF0,#B5F5D0)", action: () => navigate("/animaux") },
          ].map((item, i) => (
            <div
              key={i}
              onClick={item.action || undefined}
              className={cn(
                "rounded-3xl px-3.5 py-4 cursor-pointer border-[1.5px] border-white/85",
                "shadow-[0_4px_14px_rgba(0,0,0,0.05)] transition-transform duration-200",
                "hover:scale-[1.04] active:scale-[0.98]",
                !item.action && "opacity-50 cursor-not-allowed",
              )}
              style={{ background: item.bg }}
            >
              <div className="text-[26px] mb-1.5">{item.emoji}</div>
              <div className="font-black text-sm text-app-text">{item.label}</div>
              <div className="text-[11px] text-gray-500 font-semibold mt-0.5">{item.sub}</div>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <DisclaimerBanner />
        <div className="h-6" />
      </div>

      {/* Feedback modal */}
      {showFeedback && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in px-5" onClick={() => setShowFeedback(false)}>
          <div className="bg-white rounded-3xl w-full max-w-[400px] p-5 shadow-2xl animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-1">
              <span className="text-3xl">🧪</span>
            </div>
            <h3 className="font-black text-[17px] text-app-text text-center mb-0.5">
              PawCare est en Alpha !
            </h3>
            <p className="text-[12.5px] text-app-muted font-semibold text-center mb-4 leading-relaxed">
              Aidez Samuel a ameliorer l'app en laissant votre avis
            </p>

            {/* Stars */}
            <div className="flex justify-center gap-2 mb-3">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => { setFbRating(star); setFbSent(false); }}
                  className="bg-transparent border-none cursor-pointer text-[32px] transition-all duration-200 hover:scale-125 active:scale-95 p-0"
                  style={{
                    filter: star <= fbRating ? "none" : "grayscale(1) opacity(0.25)",
                    transform: star <= fbRating ? "scale(1.1)" : "scale(1)",
                  }}
                >
                  {"\u2B50"}
                </button>
              ))}
            </div>

            {/* Comment */}
            <textarea
              value={fbComment}
              onChange={e => { setFbComment(e.target.value); setFbSent(false); }}
              placeholder="Un bug ? Une idee ? Dites-nous tout..."
              rows={2}
              className="w-full rounded-2xl border-[1.5px] border-gray-200 bg-gray-50 px-4 py-2.5 text-[13px] font-semibold text-app-text resize-none outline-none focus:border-pawcare-orange/50 transition-colors"
            />

            <button
              onClick={submitFeedback}
              disabled={fbRating === 0 || fbLoading || fbSent}
              className="w-full mt-2.5 py-3 rounded-2xl border-none cursor-pointer font-black text-[14px] transition-all duration-200 font-sans shadow-pawcare"
              style={{
                background: fbSent ? "#E8F5E9" : "linear-gradient(135deg, #FFB347, #FF5C8D)",
                color: fbSent ? "#2E7D32" : "white",
                opacity: fbRating === 0 ? 0.5 : 1,
              }}
            >
              {fbLoading ? "Envoi..." : fbSent ? "\u2713 Merci pour votre avis !" : "Envoyer mon avis"}
            </button>

            <div className="text-center mt-2.5">
              <span className="text-[11px] text-app-subtle font-semibold">
                Cree avec amour par <span className="font-black text-pawcare-orange-dark">Samuel</span> {"\uD83D\uDC95"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
